<template>
  <b-modal
    id="clinic-detail-modal"
    ref="modal"
    size="xl"
    centered
    title="Clinic & Branch Details"
    header-bg-variant="success"
    header-text-variant="white"
    hide-footer
    @hidden="onModalHidden"
  >
    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-4">
      <b-spinner variant="success" label="Loading clinic details..."></b-spinner>
      <p class="mt-2 text-muted">Loading clinic information...</p>
    </div>

    <!-- Clinic Details Content -->
    <div v-else-if="clinic">
      <!-- Clinic Header - Prioritized -->
      <b-row class="mb-4">
        <b-col md="8">
          <div class="d-flex align-items-center">
            <div class="clinic-icon mr-3">
              <i class="fas fa-hospital fa-3x text-primary"></i>
            </div>
            <div>
              <h3 class="mb-1 text-primary">
                <strong>{{ clinic.clinicNameTh }}</strong>
              </h3>
              <p class="mb-1">
                {{ clinic.clinicNameEn || clinic.clinicNameTh }}
              </p>
              <small class="text-muted">
                Clinic Code: {{ clinic.clinicCode }}
                <span v-if="branchData"> | Branch: {{ branchData.branchNameTh }}</span>
              </small>
            </div>
          </div>
        </b-col>
        <b-col md="4" class="text-right">
          <b-button-group>
            <b-button variant="info" @click="exportClinicData">
              <i class="fas fa-download mr-1"></i>
              Export
            </b-button>
            <b-button variant="primary" @click="generateReport">
              <i class="fas fa-chart-bar mr-1"></i>
              Report
            </b-button>
          </b-button-group>
        </b-col>
      </b-row>

      <!-- Tabs -->
      <b-tabs content-class="mt-3" justified>
        <!-- Clinic Information Tab (Primary) -->
        <b-tab title="Clinic Information" active>
          <b-row>
            <b-col md="6">
              <b-card title="Clinic Details" header-bg-variant="primary" header-text-variant="white" class="mb-3">
                <dl class="row">
                  <dt class="col-sm-4">Clinic Code:</dt>
                  <dd class="col-sm-8">{{ clinic.clinicCode }}</dd>
                  
                  <dt class="col-sm-4">Thai Name:</dt>
                  <dd class="col-sm-8">{{ clinic.clinicNameTh }}</dd>
                  
                  <dt class="col-sm-4">English Name:</dt>
                  <dd class="col-sm-8">{{ clinic.clinicNameEn || '-' }}</dd>
                  
                  <dt class="col-sm-4">Tax ID:</dt>
                  <dd class="col-sm-8">{{ clinic.taxId || '-' }}</dd>
                  
                  <dt class="col-sm-4">Address:</dt>
                  <dd class="col-sm-8">{{ clinic.address || '-' }}</dd>
                  
                  <dt class="col-sm-4">Phone:</dt>
                  <dd class="col-sm-8">{{ clinic.tel || '-' }}</dd>
                  
                  <dt class="col-sm-4">Email:</dt>
                  <dd class="col-sm-8">{{ clinic.email || '-' }}</dd>
                  
                  <dt class="col-sm-4">Created:</dt>
                  <dd class="col-sm-8">{{ formatDate(clinic.creationDt) }}</dd>
                </dl>
              </b-card>
            </b-col>
            
            <b-col md="6">
              <b-card title="Statistics Overview" class="mb-3">
                <b-row v-if="statistics">
                  <b-col cols="6" class="text-center mb-3">
                    <i class="fas fa-building fa-2x text-primary mb-2"></i>
                    <h4 class="mb-0">{{ statistics?.totalBranches || 0 }}</h4>
                    <small class="text-muted">Total Branches</small>
                  </b-col>
                  
                  <b-col cols="6" class="text-center mb-3">
                    <i class="fas fa-users fa-2x text-success mb-2"></i>
                    <h4 class="mb-0">{{ statistics?.totalUsers || 0 }}</h4>
                    <small class="text-muted">Total Users</small>
                  </b-col>
                  
                  <b-col cols="6" class="text-center">
                    <i class="fas fa-user-injured fa-2x text-info mb-2"></i>
                    <h4 class="mb-0">{{ (statistics?.totalPatients || 0).toLocaleString() }}</h4>
                    <small class="text-muted">Total Patients</small>
                  </b-col>
                  
                  <b-col cols="6" class="text-center" v-if="statistics?.last30Days">
                    <i class="fas fa-dollar-sign fa-2x text-warning mb-2"></i>
                    <h4 class="mb-0">{{ formatCurrency(statistics?.last30Days?.revenue || 0) }}</h4>
                    <small class="text-muted">Revenue (30d)</small>
                  </b-col>
                </b-row>
              </b-card>
            </b-col>
          </b-row>
        </b-tab>

        <!-- Branches Tab -->
        <b-tab title="Branches">
          <div v-if="branches && branches.length > 0">
            <b-row>
              <b-col 
                v-for="branch in branches"
                :key="branch.branchId"
                md="6"
                class="mb-4"
              >
                <b-card>
                  <template #header>
                    <div class="d-flex justify-content-between align-items-center">
                      <h6 class="mb-0">
                        <i class="fas fa-clinic-medical mr-2"></i>
                        {{ branch.branchNameTh }}
                      </h6>
                      <b-badge 
                        :variant="branch.status === 1 ? 'success' : 'secondary'"
                        class="ml-2"
                      >
                        {{ branch.status === 1 ? 'Active' : 'Inactive' }}
                      </b-badge>
                    </div>
                  </template>

                  <!-- Branch Basic Info -->
                  <dl class="row mb-3">
                    <dt class="col-sm-4">Code:</dt>
                    <dd class="col-sm-8">{{ branch.branchCode }}</dd>
                    
                    <dt class="col-sm-4">English Name:</dt>
                    <dd class="col-sm-8">{{ branch.branchNameEn || '-' }}</dd>
                    
                    <dt class="col-sm-4">Address:</dt>
                    <dd class="col-sm-8">{{ branch.address || '-' }}</dd>
                    
                    <dt class="col-sm-4">Phone:</dt>
                    <dd class="col-sm-8">{{ branch.tel || '-' }}</dd>
                    
                    <dt class="col-sm-4">Email:</dt>
                    <dd class="col-sm-8">{{ branch.email || '-' }}</dd>
                    
                    <dt class="col-sm-4">Website:</dt>
                    <dd class="col-sm-8">
                      <a v-if="branch.website" :href="branch.website" target="_blank" class="text-primary">
                        {{ branch.website }}
                        <i class="fas fa-external-link-alt fa-sm ml-1"></i>
                      </a>
                      <span v-else>-</span>
                    </dd>
                  </dl>

                  <!-- Branch Statistics -->
                  <div class="branch-stats bg-light p-3 rounded">
                    <b-row class="text-center">
                      <b-col cols="6">
                        <strong class="text-primary">{{ branch.memberCount || 0 }}</strong>
                        <br>
                        <small class="text-muted">Members</small>
                      </b-col>
                      <b-col cols="6">
                        <strong class="text-success">{{ (branch.patientCount || 0).toLocaleString() }}</strong>
                        <br>
                        <small class="text-muted">Patients</small>
                      </b-col>
                    </b-row>
                  </div>

                  <!-- Branch Members (if available) -->
                  <div v-if="branch.members && branch.members.length > 0" class="mt-3">
                    <h6 class="border-top pt-3">
                      <i class="fas fa-users mr-2"></i>
                      Members
                    </h6>
                    <div class="member-list">
                      <div
                        v-for="member in branch.members.slice(0, 5)"
                        :key="member.uid"
                        class="d-flex justify-content-between align-items-center py-1"
                      >
                        <div>
                          <strong>{{ member.firstNameTh }} {{ member.lastNameTh }}</strong>
                          <br>
                          <small class="text-muted">{{ member.email }}</small>
                        </div>
                        <div>
                          <b-badge :variant="member.userTypeId === 1 ? 'primary' : 'secondary'" class="mr-1">
                            {{ member.userTypeId === 1 ? 'Doctor' : 'Staff' }}
                          </b-badge>
                          <span v-if="member.authIdItems" class="text-muted">
                            <small>{{ getPermissionSummary(member.authIdItems) }}</small>
                          </span>
                        </div>
                      </div>
                      <div v-if="branch.members.length > 5" class="text-center mt-2">
                        <small class="text-muted">
                          +{{ branch.members.length - 5 }} more members
                        </small>
                      </div>
                    </div>
                  </div>
                </b-card>
              </b-col>
            </b-row>
          </div>
          <div v-else class="text-center py-4">
            <i class="fas fa-clinic-medical fa-3x text-muted mb-3"></i>
            <h5 class="text-muted">No Branches Found</h5>
            <p class="text-muted">This clinic doesn't have any branches yet.</p>
          </div>
        </b-tab>

        <!-- Members Tab -->
        <b-tab title="Members">
          <div v-if="allMembers && allMembers.length > 0">
            <!-- Members Summary -->
            <b-row class="mb-3">
              <b-col md="6">
                <h5>
                  <i class="fas fa-users text-primary mr-2"></i>
                  Total Members: {{ allMembers.length }}
                </h5>
              </b-col>
              <b-col md="6" class="text-right">
                <b-button variant="outline-primary" size="sm" @click="exportMemberData">
                  <i class="fas fa-download mr-1"></i>
                  Export Members
                </b-button>
              </b-col>
            </b-row>

            <!-- Members Search -->
            <b-row class="mb-3">
              <b-col md="4">
                <b-form-input
                  v-model="memberSearchTerm"
                  placeholder="Search members..."
                  size="sm"
                >
                  <template #prepend>
                    <b-input-group-text>
                      <i class="fas fa-search"></i>
                    </b-input-group-text>
                  </template>
                </b-form-input>
              </b-col>
              <b-col md="3">
                <b-form-select
                  v-model="memberTypeFilter"
                  :options="memberTypeOptions"
                  size="sm"
                ></b-form-select>
              </b-col>
              <b-col md="3">
                <b-form-select
                  v-model="memberStatusFilter"
                  :options="memberStatusOptions"
                  size="sm"
                ></b-form-select>
              </b-col>
              <b-col md="2">
                <b-button variant="outline-secondary" size="sm" @click="clearMemberFilters">
                  <i class="fas fa-times mr-1"></i>
                  Clear
                </b-button>
              </b-col>
            </b-row>

            <!-- Members Table -->
            <b-table
              :items="filteredMembers"
              :fields="memberTableFields"
              :per-page="membersPerPage"
              :current-page="currentMemberPage"
              striped
              hover
              responsive
              show-empty
              empty-text="No members found matching your criteria"
              class="member-table"
            >
              <!-- Name Column -->
              <template #cell(name)="data">
                <div>
                  <strong>{{ data.item.firstNameTh }} {{ data.item.lastNameTh }}</strong>
                  <br>
                  <small class="text-muted">{{ data.item.firstNameEn }} {{ data.item.lastNameEn }}</small>
                </div>
              </template>

              <!-- Contact Column -->
              <template #cell(contact)="data">
                <div>
                  <div class="mb-1">
                    <i class="fas fa-envelope fa-sm text-muted mr-1"></i>
                    <small>{{ data.item.email }}</small>
                  </div>
                  <div v-if="data.item.mobile">
                    <i class="fas fa-phone fa-sm text-muted mr-1"></i>
                    <small>{{ data.item.mobile }}</small>
                  </div>
                </div>
              </template>

              <!-- Type & Status Column -->
              <template #cell(typeStatus)="data">
                <div>
                  <b-badge :variant="data.item.userTypeId === 1 ? 'primary' : 'secondary'" class="mb-1">
                    {{ data.item.userTypeId === 1 ? 'Doctor' : 'Staff' }}
                  </b-badge>
                  <br>
                  <b-badge :variant="data.item.statusId === 1 ? 'success' : 'secondary'">
                    {{ data.item.statusId === 1 ? 'Active' : 'Inactive' }}
                  </b-badge>
                </div>
              </template>

              <!-- License & Specialties Column -->
              <template #cell(professional)="data">
                <div v-if="data.item.userTypeId === 1">
                  <div v-if="data.item.drLicenseNo" class="mb-1">
                    <i class="fas fa-certificate fa-sm text-success mr-1"></i>
                    <small>{{ data.item.drLicenseNo }}</small>
                  </div>
                  <div v-if="data.item.drSpecialItems && data.item.drSpecialItems.length > 0">
                    <b-badge
                      v-for="specialtyId in data.item.drSpecialItems.slice(0, 2)"
                      :key="specialtyId"
                      variant="outline-info"
                      class="mr-1 mb-1"
                      size="sm"
                    >
                      {{ getSpecialtyName(specialtyId) }}
                    </b-badge>
                    <small v-if="data.item.drSpecialItems.length > 2" class="text-muted">
                      +{{ data.item.drSpecialItems.length - 2 }} more
                    </small>
                  </div>
                  <div v-if="data.item.lastTdcVerifiedAt" class="mt-1">
                    <small class="text-muted">
                      <i class="fas fa-check-circle fa-sm text-success mr-1"></i>
                      Verified: {{ formatDate(data.item.lastTdcVerifiedAt) }}
                    </small>
                  </div>
                </div>
                <div v-else class="text-muted">
                  <small>N/A</small>
                </div>
              </template>

              <!-- Permissions Column -->
              <template #cell(permissions)="data">
                <div>
                  <b-badge
                    v-for="authId in data.item.authIdItems.slice(0, 3)"
                    :key="authId"
                    :variant="getPermissionVariant(authId)"
                    class="mr-1 mb-1"
                    size="sm"
                  >
                    {{ getPermissionName(authId) }}
                  </b-badge>
                  <br v-if="data.item.authIdItems.length > 3">
                  <small v-if="data.item.authIdItems.length > 3" class="text-muted">
                    +{{ data.item.authIdItems.length - 3 }} more
                  </small>
                </div>
              </template>

              <!-- Branch Column -->
              <template #cell(branch)="data">
                <div>
                  <strong class="text-primary">{{ data.item.branchNameTh }}</strong>
                  <br>
                  <small class="text-muted">{{ data.item.branchCode }}</small>
                </div>
              </template>

              <!-- Member Since Column -->
              <template #cell(memberSince)="data">
                <small>{{ formatDate(data.item.membershipCreated) }}</small>
              </template>
            </b-table>

            <!-- Pagination -->
            <b-pagination
              v-if="filteredMembers.length > membersPerPage"
              v-model="currentMemberPage"
              :total-rows="filteredMembers.length"
              :per-page="membersPerPage"
              align="center"
              size="sm"
              class="mt-3"
            ></b-pagination>
          </div>
          <div v-else class="text-center py-4">
            <i class="fas fa-users fa-3x text-muted mb-3"></i>
            <h5 class="text-muted">No Members Found</h5>
            <p class="text-muted">This clinic doesn't have any members yet.</p>
          </div>
        </b-tab>

        <!-- Activity & Analytics Tab -->
        <b-tab title="Analytics" v-if="statistics">
          <!-- Key Metrics Cards -->
          <b-row class="mb-4">
            <b-col md="3">
              <b-card text-variant="white" bg-variant="primary" class="mb-3">
                <b-card-text class="text-center">
                  <i class="fas fa-calendar-check fa-2x mb-2"></i>
                  <h3 class="mb-0">{{ (statistics?.last30Days?.appointments || 0).toLocaleString() }}</h3>
                  <small>Appointments (30d)</small>
                </b-card-text>
              </b-card>
            </b-col>
            
            <b-col md="3">
              <b-card text-variant="white" bg-variant="success" class="mb-3">
                <b-card-text class="text-center">
                  <i class="fas fa-receipt fa-2x mb-2"></i>
                  <h3 class="mb-0">{{ (statistics?.last30Days?.receipts || 0).toLocaleString() }}</h3>
                  <small>Receipts (30d)</small>
                </b-card-text>
              </b-card>
            </b-col>
            
            <b-col md="3">
              <b-card text-variant="white" bg-variant="warning" class="mb-3">
                <b-card-text class="text-center">
                  <i class="fas fa-dollar-sign fa-2x mb-2"></i>
                  <h3 class="mb-0">{{ formatCurrency(statistics?.last30Days?.revenue || 0) }}</h3>
                  <small>Revenue (30d)</small>
                </b-card-text>
              </b-card>
            </b-col>
            
            <b-col md="3">
              <b-card text-variant="white" bg-variant="info" class="mb-3">
                <b-card-text class="text-center">
                  <i class="fas fa-chart-line fa-2x mb-2"></i>
                  <h3 class="mb-0">{{ formatCurrency((statistics?.last30Days?.revenue || 0) / (statistics?.last30Days?.receipts || 1)) }}</h3>
                  <small>Avg Receipt</small>
                </b-card-text>
              </b-card>
            </b-col>
          </b-row>

          <!-- Activity Summary -->
          <b-card title="Recent Activity Summary">
            <b-row>
              <b-col md="6">
                <h6>Last 30 Days Performance</h6>
                <ul class="list-unstyled">
                  <li class="mb-2">
                    <i class="fas fa-calendar-check text-primary mr-2"></i>
                    <strong>{{ (statistics?.last30Days?.appointments || 0).toLocaleString() }}</strong> appointments scheduled
                  </li>
                  <li class="mb-2">
                    <i class="fas fa-user-plus text-success mr-2"></i>
                    <strong>{{ (statistics?.last30Days?.newPatients || 0).toLocaleString() }}</strong> new patients registered
                  </li>
                  <li class="mb-2">
                    <i class="fas fa-receipt text-warning mr-2"></i>
                    <strong>{{ (statistics?.last30Days?.receipts || 0).toLocaleString() }}</strong> receipts generated
                  </li>
                </ul>
              </b-col>
              
              <b-col md="6">
                <h6>Overall Statistics</h6>
                <ul class="list-unstyled">
                  <li class="mb-2">
                    <i class="fas fa-building text-primary mr-2"></i>
                    Operating across <strong>{{ statistics?.totalBranches || 0 }}</strong> branches
                  </li>
                  <li class="mb-2">
                    <i class="fas fa-users text-success mr-2"></i>
                    <strong>{{ statistics?.totalUsers || 0 }}</strong> active staff members
                  </li>
                  <li class="mb-2">
                    <i class="fas fa-user-injured text-info mr-2"></i>
                    Serving <strong>{{ (statistics?.totalPatients || 0).toLocaleString() }}</strong> total patients
                  </li>
                </ul>
              </b-col>
            </b-row>
          </b-card>
        </b-tab>
      </b-tabs>

      <!-- Modal Actions -->
      <div class="mt-4 text-right">
        <b-button variant="secondary" @click="closeModal">
          Close
        </b-button>
      </div>
    </div>

    <!-- Error State -->
    <div v-else class="text-center py-4">
      <i class="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
      <h5 class="text-muted">Failed to load clinic details</h5>
      <p class="text-muted">Please try again or contact support if the problem persists.</p>
      <b-button variant="primary" @click="closeModal">
        Close
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import moment from 'moment';

export default {
  name: "ClinicDetailModal",
  data() {
    return {
      branchData: null,
      permissionNames: {
        1: 'Manager',
        2: 'Doctor', 
        3: 'Counter',
        4: 'Assistant',
        5: 'Head Counter'
      },
      specialtyNames: {
        1: 'General Dentistry',
        2: 'Orthodontics',
        3: 'Oral Surgery',
        4: 'Periodontics',
        5: 'Endodontics',
        6: 'Prosthodontics',
        7: 'Pediatric Dentistry'
      },
      // Member table filters
      memberSearchTerm: '',
      memberTypeFilter: '',
      memberStatusFilter: '',
      membersPerPage: 10,
      currentMemberPage: 1,
      memberTableFields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'contact', label: 'Contact', sortable: false },
        { key: 'typeStatus', label: 'Type & Status', sortable: true },
        { key: 'professional', label: 'License & Specialties', sortable: false },
        { key: 'permissions', label: 'Permissions', sortable: false },
        { key: 'branch', label: 'Branch', sortable: true },
        { key: 'memberSince', label: 'Member Since', sortable: true }
      ],
      memberTypeOptions: [
        { value: '', text: 'All Types' },
        { value: 1, text: 'Doctors' },
        { value: 2, text: 'Staff' }
      ],
      memberStatusOptions: [
        { value: '', text: 'All Status' },
        { value: 1, text: 'Active' },
        { value: 0, text: 'Inactive' }
      ]
    };
  },
  computed: {
    ...mapGetters('moduleSuperadmin', [
      'getSelectedClinic',
      'isClinicDetailsLoading'
    ]),
    clinic() {
      // Use branch data if available, otherwise fall back to selected clinic
      if (this.branchData) {
        return {
          clinicCode: this.branchData.clinicCode,
          clinicNameTh: this.branchData.clinicNameTh,
          clinicNameEn: this.branchData.clinicNameEn,
          address: this.branchData.address,
          tel: this.branchData.tel,
          email: this.branchData.email,
          creationDt: this.branchData.creationDt
        };
      }
      return this.getSelectedClinic?.clinic;
    },
    branches() {
      // If showing a single branch, return it as an array
      if (this.branchData) {
        return [{
          branchCode: this.branchData.branchCode,
          branchNameTh: this.branchData.branchNameTh,
          branchNameEn: this.branchData.branchNameEn,
          address: this.branchData.address,
          tel: this.branchData.tel,
          email: this.branchData.email,
          status: this.branchData.status,
          memberCount: this.branchData.memberCount,
          members: this.branchData.members || []
        }];
      }
      return this.getSelectedClinic?.clinic?.branches;
    },
    statistics() {
      // Use statistics from parent clinic if available
      if (this.branchData && this.branchData.parentClinic) {
        return this.branchData.parentClinic.statistics;
      }
      return this.getSelectedClinic?.clinic?.statistics;
    },
    isLoading() {
      // No loading when using branch data directly
      if (this.branchData) {
        return false;
      }
      return this.isClinicDetailsLoading;
    },
    allMembers() {
      // Aggregate all members from all branches
      const members = [];
      const branches = this.branches || [];
      
      branches.forEach(branch => {
        if (branch.members && branch.members.length > 0) {
          branch.members.forEach(member => {
            members.push({
              ...member,
              // Add branch information to each member
              branchId: branch.branchId,
              branchCode: branch.branchCode,
              branchNameTh: branch.branchNameTh,
              branchNameEn: branch.branchNameEn
            });
          });
        }
      });
      
      return members;
    },
    filteredMembers() {
      let filtered = [...this.allMembers];
      
      // Apply search filter
      if (this.memberSearchTerm) {
        const searchTerm = this.memberSearchTerm.toLowerCase();
        filtered = filtered.filter(member => 
          member.firstNameTh.toLowerCase().includes(searchTerm) ||
          member.lastNameTh.toLowerCase().includes(searchTerm) ||
          member.firstNameEn.toLowerCase().includes(searchTerm) ||
          member.lastNameEn.toLowerCase().includes(searchTerm) ||
          member.email.toLowerCase().includes(searchTerm) ||
          (member.mobile && member.mobile.toLowerCase().includes(searchTerm)) ||
          (member.drLicenseNo && member.drLicenseNo.toLowerCase().includes(searchTerm))
        );
      }
      
      // Apply type filter
      if (this.memberTypeFilter !== '') {
        filtered = filtered.filter(member => member.userTypeId === this.memberTypeFilter);
      }
      
      // Apply status filter
      if (this.memberStatusFilter !== '') {
        filtered = filtered.filter(member => member.statusId === this.memberStatusFilter);
      }
      
      return filtered;
    }
  },
  methods: {
    ...mapActions('moduleSuperadmin', [
      'fetchClinicDetails',
      'clearSelectedClinic'
    ]),
    
    async show(clinicCode) {
      this.$bvModal.show('clinic-detail-modal');
      await this.fetchClinicDetails(clinicCode);
    },
    
    showBranch(branch) {
      // Show modal with branch data directly (no fetch needed)
      this.branchData = branch;
      this.$bvModal.show('clinic-detail-modal');
    },
    
    closeModal() {
      this.$bvModal.hide('clinic-detail-modal');
    },
    
    onModalHidden() {
      this.branchData = null;
      this.clearSelectedClinic();
    },
    
    exportClinicData() {
      // Create CSV data
      const csvData = this.generateClinicCSV();
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `clinic_${this.clinic.clinicCode}_details.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    
    generateReport() {
      this.$bvToast.toast('Report generation feature coming soon', {
        title: 'Feature Coming Soon',
        variant: 'info',
        autoHideDelay: 3000
      });
    },
    
    generateClinicCSV() {
      const clinic = this.clinic;
      const branches = this.branches || [];
      
      let csv = 'Clinic Information\n';
      csv += 'Field,Value\n';
      csv += `Clinic Code,${clinic.clinicCode}\n`;
      csv += `Name (Thai),${clinic.clinicNameTh}\n`;
      csv += `Name (English),${clinic.clinicNameEn || ''}\n`;
      csv += `Tax ID,${clinic.taxId || ''}\n`;
      csv += `Address,${clinic.address || ''}\n`;
      csv += `Phone,${clinic.tel || ''}\n`;
      csv += `Email,${clinic.email || ''}\n`;
      csv += `Created,${this.formatDate(clinic.creationDt)}\n`;
      
      if (this.statistics) {
        csv += `Total Branches,${this.statistics.totalBranches || 0}\n`;
        csv += `Total Users,${this.statistics.totalUsers || 0}\n`;
        csv += `Total Patients,${this.statistics.totalPatients || 0}\n`;
      }
      
      csv += '\n\nBranches\n';
      csv += 'Branch Code,Branch Name,Status,Address,Phone,Email,Members,Patients\n';
      
      branches.forEach(branch => {
        csv += `${branch.branchCode},"${branch.branchNameTh}",${branch.status === 1 ? 'Active' : 'Inactive'},"${branch.address || ''}",${branch.tel || ''},${branch.email || ''},${branch.memberCount || 0},${branch.patientCount || 0}\n`;
      });
      
      return csv;
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '-';
      return moment(dateStr).format('YYYY-MM-DD HH:mm:ss');
    },
    
    formatCurrency(amount) {
      if (!amount || amount === 0) return '฿0';
      return new Intl.NumberFormat('th-TH', {
        style: 'currency',
        currency: 'THB',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    },
    
    getPermissionSummary(authIdItems) {
      if (!authIdItems) return '';
      
      // Parse the comma-separated string if it's a string
      let permissions = Array.isArray(authIdItems) ? authIdItems : authIdItems.split(',').map(Number);
      
      // Get permission names
      const permissionNames = permissions.map(id => this.permissionNames[id]).filter(Boolean);
      
      // Return first 2 permissions with "..." if more
      if (permissionNames.length > 2) {
        return permissionNames.slice(0, 2).join(', ') + '...';
      }
      return permissionNames.join(', ');
    },

    // Member-specific methods
    getSpecialtyName(specialtyId) {
      return this.specialtyNames[specialtyId] || `Specialty ${specialtyId}`;
    },
    
    getPermissionName(authId) {
      return this.permissionNames[authId] || `Permission ${authId}`;
    },
    
    getPermissionVariant(authId) {
      const variants = {
        1: 'danger',    // Manager - red
        2: 'primary',   // Doctor - blue  
        3: 'success',   // Counter - green
        4: 'info',      // Assistant - cyan
        5: 'warning'    // Head Counter - yellow
      };
      return variants[authId] || 'secondary';
    },

    clearMemberFilters() {
      this.memberSearchTerm = '';
      this.memberTypeFilter = '';
      this.memberStatusFilter = '';
      this.currentMemberPage = 1;
    },

    exportMemberData() {
      const csvData = this.generateMemberCSV();
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `clinic_${this.clinic.clinicCode}_members.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },

    generateMemberCSV() {
      const members = this.allMembers;
      
      let csv = 'Clinic Members Export\n';
      csv += 'Generated on: ' + new Date().toISOString() + '\n';
      csv += 'Clinic: ' + this.clinic.clinicNameTh + ' (' + this.clinic.clinicCode + ')\n\n';
      
      csv += 'Member ID,Name (Thai),Name (English),Email,Mobile,User Type,Status,License No,Specialties,Permissions,Branch Code,Branch Name,Member Since\n';
      
      members.forEach(member => {
        const specialties = (member.drSpecialItems || []).map(id => this.getSpecialtyName(id)).join(';');
        const permissions = (member.authIdItems || []).map(id => this.getPermissionName(id)).join(';');
        
        csv += `${member.uid},"${member.firstNameTh} ${member.lastNameTh}","${member.firstNameEn} ${member.lastNameEn}",${member.email},${member.mobile || ''},${member.userTypeId === 1 ? 'Doctor' : 'Staff'},${member.statusId === 1 ? 'Active' : 'Inactive'},${member.drLicenseNo || ''},"${specialties}","${permissions}",${member.branchCode},"${member.branchNameTh}",${this.formatDate(member.membershipCreated)}\n`;
      });
      
      return csv;
    }
  }
};
</script>

<style scoped>
.clinic-detail-modal .modal-header {
  border-bottom: 1px solid #dee2e6;
}

.clinic-detail-modal .card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  font-weight: 600;
}

.clinic-detail-modal .dl-horizontal dt {
  font-weight: 600;
  color: #495057;
}

.clinic-detail-modal .clinic-icon {
  flex-shrink: 0;
}

.clinic-detail-modal .badge {
  font-size: 0.75rem;
}

.clinic-detail-modal .card-text h3 {
  font-size: 1.5rem;
  font-weight: 700;
}

.clinic-detail-modal .card-text h4 {
  font-size: 1.25rem;
  font-weight: 600;
}

.clinic-detail-modal .card-text small {
  font-size: 0.875rem;
  opacity: 0.8;
}

.clinic-detail-modal .fa-2x {
  font-size: 2rem;
}

.clinic-detail-modal .fa-3x {
  font-size: 3rem;
}

.clinic-detail-modal .branch-stats {
  border: 1px solid #e9ecef;
}

.clinic-detail-modal .member-list {
  max-height: 200px;
  overflow-y: auto;
}

.clinic-detail-modal .member-list .d-flex {
  border-bottom: 1px solid #f8f9fa;
}

.clinic-detail-modal .member-list .d-flex:last-child {
  border-bottom: none;
}

/* Tab content spacing */
.clinic-detail-modal .tab-content {
  padding-top: 1rem;
}

/* Member table specific styles */
.clinic-detail-modal .member-table {
  font-size: 0.875rem;
}

.clinic-detail-modal .member-table th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #495057;
  white-space: nowrap;
}

.clinic-detail-modal .member-table td {
  vertical-align: middle;
  padding: 0.75rem 0.5rem;
}

.clinic-detail-modal .member-table .badge {
  font-size: 0.7rem;
  font-weight: 500;
}

.clinic-detail-modal .member-table small {
  font-size: 0.75rem;
  line-height: 1.2;
}

/* Search and filter row */
.clinic-detail-modal .b-form-input,
.clinic-detail-modal .b-form-select {
  border-color: #ced4da;
  border-radius: 0.25rem;
}

.clinic-detail-modal .b-form-input:focus,
.clinic-detail-modal .b-form-select:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .clinic-detail-modal .dl-horizontal dt {
    font-size: 0.875rem;
  }
  
  .clinic-detail-modal .dl-horizontal dd {
    font-size: 0.875rem;
  }
  
  .clinic-detail-modal .card-text h3 {
    font-size: 1.25rem;
  }
  
  .clinic-detail-modal .card-text h4 {
    font-size: 1.1rem;
  }
  
  .clinic-detail-modal .member-table {
    font-size: 0.75rem;
  }
  
  .clinic-detail-modal .member-table td {
    padding: 0.5rem 0.25rem;
  }
  
  .clinic-detail-modal .member-table .badge {
    font-size: 0.65rem;
  }
}
</style>